import { Form, Formik, FormikProps } from "formik";

import { Button, ButtonsWrapper, Info } from "components/elements";
import { Row, Column } from "components/layout";
import { Modal } from "components/modals";
import { TiVoTargets } from "hooks/tivoApps";
import { trans } from "src/translations";
import { FieldProps } from "types/form";

import { TargetSearchList, TargetSummary } from "./components";
import { TEXTS } from "./constants";
import styles from "./TiVoDevicesModal.scss";
import { TiVoTargetsForm } from "./types";
import { useTiVoDeviceData } from "./useTiVoDeviceData";
import {
  getFieldsSetup,
  getInitialValues,
  getSelectedPlatformTypes,
} from "./utils";

interface TiVoDevicesModalProps {
  /** Fields (columns) that we want to handle */
  fields: FieldProps["fields"];
  /** Modal visibility */
  isOpen: boolean;
  /** Initial target configuration to populate the form with */
  targets?: TiVoTargets;
  /** Target to which selection will be restricted. User will be only shown elements from provided target */
  restrictTargets?: TiVoTargets;
  /** Enables selecting all targets with same slug across groups */
  enableSlugBulkSelection?: boolean;
  dataTestId?: string;

  onClose: () => void;
  onSave: (targets: TiVoTargetsForm) => void;
}

export const TiVoDevicesModal = ({
  fields,
  isOpen,
  targets = {},
  restrictTargets,
  enableSlugBulkSelection = false,
  dataTestId = "tivo-devices-modal",
  onClose,
  onSave,
}: TiVoDevicesModalProps) => {
  const tivoDeviceData = useTiVoDeviceData();

  return (
    <Modal
      className={styles.container}
      isOpen={isOpen}
      size="huge"
      title={TEXTS.MODAL_HEADER}
      dataTestId={dataTestId}
      onClose={onClose}
    >
      <Formik
        initialValues={getInitialValues(
          targets,
          tivoDeviceData,
          restrictTargets
        )}
        onSubmit={onSave}
      >
        {({ values }: FormikProps<TiVoTargetsForm>) => {
          const columns = getFieldsSetup(
            fields,
            values,
            tivoDeviceData,
            restrictTargets,
            enableSlugBulkSelection
          );
          const platformTypeConflict =
            getSelectedPlatformTypes(values, tivoDeviceData.getPlatformType)
              .length > 1;

          return (
            <Form>
              <Row>
                {columns.map((col) => (
                  <Column key={`et-col-${col.listName}`}>
                    <TargetSearchList
                      {...col}
                      dataTestId={`${dataTestId}-${col.listName}-search`}
                    />
                    <TargetSummary
                      {...col}
                      dataTestId={`${dataTestId}-${col.listName}-summary`}
                    />
                  </Column>
                ))}
              </Row>
              {platformTypeConflict && (
                <Info className={styles.error} type="error">
                  {TEXTS.PLATFORM_TYPE_CONFLICT_MESSAGE}
                </Info>
              )}
              <ButtonsWrapper>
                <Button
                  dataTestId={`${dataTestId}-cancel-button`}
                  onClick={onClose}
                >
                  {trans.CANCEL()}
                </Button>
                <Button
                  buttonType="submit"
                  disabled={platformTypeConflict}
                  type="green"
                  dataTestId={`${dataTestId}-save-button`}
                >
                  {trans.SAVE()}
                </Button>
              </ButtonsWrapper>
            </Form>
          );
        }}
      </Formik>
    </Modal>
  );
};
